import { Modal, Tag } from 'antd'
import Table, { ColumnsType } from 'antd/es/table'
import React, { FC } from 'react'
import styled from 'styled-components'

import {
  FAMILY_LABELS,
  Y_304_TABLE,
  Y_TEMPS_C,
  YMaterialFamily,
} from './y304Table'

const HintText = styled.span`
  font-size: 0.8rem;
  opacity: 0.75;
`

interface FamilyRow {
  family: YMaterialFamily
  label: string
  values: readonly (number | null)[]
}

const ROWS: readonly FamilyRow[] = (
  Object.keys(Y_304_TABLE) as YMaterialFamily[]
).map((family) => ({
  family,
  label: FAMILY_LABELS[family],
  values: Y_304_TABLE[family],
}))

export interface Y304TableModalProps {
  open: boolean
  onClose: () => void
  /** Called with the chosen Y (a discrete table cell). */
  onPick: (y: number) => void
}

/**
 * ASME B31.3 Table 304.1.1 picker (thin-wall `t < D/6`). Mirrors the allowable-stress picker:
 * a family × metal-temperature grid where each cell is a clickable value. Nothing is written
 * until the user clicks — no reactive auto-fill.
 */
export const Y304TableModal: FC<Y304TableModalProps> = ({
  open,
  onClose,
  onPick,
}) => {
  const pick = (y: number): void => {
    onPick(y)
    onClose()
  }

  // Small static grid (5 rows × 8 temps) — no memoization needed.
  const tempCols: ColumnsType<FamilyRow> = Y_TEMPS_C.map((temp, i) => ({
    title:
      i === 0 ? `≤${temp}` : i === Y_TEMPS_C.length - 1 ? `≥${temp}` : temp,
    key: `t-${temp}`,
    width: 64,
    align: 'center' as const,
    render: (_v, row) => {
      const value = row.values[i]
      if (value === null) {
        return <span style={{ opacity: 0.4 }}>—</span>
      }
      return (
        <Tag.CheckableTag checked={false} onChange={() => pick(value)}>
          {value.toFixed(1)}
        </Tag.CheckableTag>
      )
    },
  }))
  const columns: ColumnsType<FamilyRow> = [
    {
      title: 'Material family',
      dataIndex: 'label',
      key: 'family',
      fixed: 'left',
      width: 220,
    },
    ...tempCols,
  ]

  return (
    <Modal
      title='Pick coefficient Y — ASME B31.3 Table 304.1.1'
      open={open}
      onCancel={onClose}
      onOk={onClose}
      footer={
        <HintText>
          Values by material family and <strong>metal temperature (°C)</strong>.
          Click a cell to use it.
        </HintText>
      }
      width={900}>
      <Table
        columns={columns}
        dataSource={ROWS}
        rowKey={(row) => row.family}
        pagination={false}
        size='small'
        scroll={{ x: 'max-content' }}
      />
    </Modal>
  )
}
